import { FC, useContext, useMemo, useState, useEffect } from 'react';
import {
	Box,
	Image,
	Text,
	Button,
	Flex,
	HStack,
	Avatar,
} from 'src/components/ui';
import {
	ICreative,
	isCreatomateAttributes,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import UserContext from 'src/contexts/user/UserContext';
import { CampaignContext } from 'src/contexts';
import CreativeCard from '../CreativeCard';
import MiniGlobeIcon from 'src/assets/icons/MiniGlobe';
import { IChannelCreativeAttributes } from 'src/lib/schemas';
import { formatCallToAction } from '../utils/formatedCTA';
import DDPhotoEditor, {
	DDPhotoEditorProps,
} from '../../design-directions/DDPhotoEditor';
import FusionLoading from 'src/components/common/FusionLoading';
import TemplatePreview from 'src/components/templates/TemplatePreview';

interface SquareCreativeProps extends Partial<DDPhotoEditorProps> {
	creative: ICreative;
	withActions?: boolean;
	size?: string;
	creativesConfig?: IChannelCreativeAttributes[];
	editMode?: boolean;
	isPaid?: boolean;
}

const SquareCreative: FC<SquareCreativeProps> = ({
	creative,
	withActions = true,
	size = 'md',
	creativesConfig,
	editMode = false,
	isPaid,
	...photoEditorProps
}) => {
	const { account } = useContext(UserContext);
	const { campaign } = useContext(CampaignContext);
	const destination = campaign?.destination ?? '';
	const { primaryText, headline, callToAction } = (creative as any).attributes;
	const [logo, setLogo] = useState<string | undefined>(account?.logoDark || account?.logoSquare);
	const isPending =
		creative.status === 'pending' || creative.status === 'generating';
	const cleanedDomain = destination
		.replace(/(^\w+:|^)\/\//, '')
		.replace(/^www\./, '')
		.split('/')[0];

	const isCreatomate =
		creative.adEngine === 'creatomate' &&
		isCreatomateAttributes(creative.attributes);
	const isFbFeed =
		creative.adEngine === 'creatomate' &&
		(creative.mediaType === 'image.feed.fb' ||
			creative.mediaType === 'image.feedPaid.fb');

	const isIgFeed =
		creative.adEngine === 'creatomate' &&
		(creative.mediaType === 'image.feed.ig' ||
			creative.mediaType === 'image.feedPaid.ig');

	let previewWidth: any = 'auto';
	let previewHeight: any = 'auto';

	if (isFbFeed) {
		previewWidth = { base: '100%', sm: '382px' };
		previewHeight = { base: 'auto', sm: '382px' };
	} else if (isIgFeed) {
		previewWidth = { base: '100%', sm: 'auto' };
		previewHeight = { base: 'auto', sm: '475px' };
	}
	const normalizedTemplate = useMemo(() => {
		if (isCreatomate) {
			const clone = JSON.parse(JSON.stringify(creative.attributes));
			if (Array.isArray(clone.elements)) {
				clone.elements = clone.elements.map((el: any) => {
					if (typeof el.x === 'string') el.x = el.x.trim();
					if (typeof el.y === 'string') el.y = el.y.trim();
					if (typeof el.width === 'string') el.width = el.width.trim();
					if (typeof el.height === 'string') el.height = el.height.trim();
					if (el.elements && Array.isArray(el.elements)) {
						el.elements = el.elements.map((subEl: any) => {
							if (typeof subEl.x === 'string') subEl.x = subEl.x.trim();
							if (typeof subEl.y === 'string') subEl.y = subEl.y.trim();
							if (typeof subEl.width === 'string')
								subEl.width = subEl.width.trim();
							if (typeof subEl.height === 'string')
								subEl.height = subEl.height.trim();
							return subEl;
						});
					}
					return el;
				});
			}
			return clone;
		}
		return null;
	}, [JSON.stringify((creative as any).attributes.elements)]);

	useEffect(() => {
		setLogo(account?.logoDark || account?.logoSquare);
	}, [account]);

	return (
		<CreativeCard
			creative={creative}
			withActions={withActions}
			size={size}
			title="Feed"
			creativesConfig={creativesConfig}
		>
			{isPending ? (
				<Flex width="100%" minH={{ base: '400px', md: '756px' }} justifyContent="center">
					<FusionLoading isLoading />
				</Flex>
			) : (
				<>
					<Box p={4}>
						<Flex alignItems="center" mb={4} gap={2}>
							<Avatar
								key={logo || 'no-logo'}
								src={logo}
								sx={{
									img: { objectFit: 'contain' },
								}}
							/>
							<Box>
								<Text fontWeight="bold">{account?.name}</Text>
								<HStack spacing={1}>
									{isPaid && (
										<>
											<Text color="black" fontSize="sm">
												Sponsored ·
											</Text>
											<MiniGlobeIcon />
										</>
									)}
								</HStack>
							</Box>
						</Flex>
						<Text mb={2} noOfLines={2} overflow="hidden">
							{primaryText}
						</Text>
						<Text color="blue.500" isTruncated>
							{destination}
						</Text>
					</Box>
					{isCreatomate && normalizedTemplate ? (
						<Box w={previewWidth} h={previewHeight}>
							<TemplatePreview
								key={JSON.stringify((creative as any).attributes?.elements)}
								template={normalizedTemplate}
							/>
						</Box>
					) : !editMode ? (
						<Image
							key={(creative as any).attributes.image?.flatFile}
							src={(creative as any).attributes.image?.flatFile}
							alt="Preview"
							fallbackSrc="/fusion-loader.gif"
						/>
					) : (
						photoEditorProps.designDirection &&
						photoEditorProps.isChangingLayer !== undefined &&
						photoEditorProps.onRefetchCreatives && (
							<DDPhotoEditor
								key={photoEditorProps.layeredFile}
								layeredFile={photoEditorProps.layeredFile}
								textLayersContent={photoEditorProps.textLayersContent}
								mediaLayersContent={photoEditorProps.mediaLayersContent}
								fonts={photoEditorProps.fonts}
								designDirection={photoEditorProps.designDirection}
								isChangingLayer={photoEditorProps.isChangingLayer}
								onRefetchCreatives={photoEditorProps.onRefetchCreatives}
								withActions={false}
								containerHeight="382px"
							/>
						)
					)}
					<HStack
						p={4}
						bg="gray.50"
						borderTop="1px solid"
						borderColor="gray.200"
						borderBottomRadius="lg"
					>
						<Box maxW="70%">
							<Text fontSize="sm" color="gray.500" isTruncated>
								{cleanedDomain}
							</Text>
							<Text
								data-testid="creative-headline"
								fontWeight="bold"
								noOfLines={1}
							>
								{headline}
							</Text>
							<Text fontSize="sm" color="gray.500" noOfLines={2}>
								{primaryText}
							</Text>
						</Box>
						{callToAction && (
							<Button
								size="sm"
								cursor="default"
								bg="gray.100"
								minW="fit-content"
								px={3}
								_hover={{ bg: 'gray.100' }}
								fontWeight={500}
								ml="auto"
							>
								{formatCallToAction(callToAction)}
							</Button>
						)}
					</HStack>
				</>
			)}
		</CreativeCard>
	);
};

export default SquareCreative;
